import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@munawb/hooks';
import { fetchExecutiveDashboard } from '@munawb/supabase';
import { supabase } from '@munawb/supabase';
import type { ExecutiveDashboardResponse } from '@munawb/types';
import { RISK_LEVEL_COLORS } from '../lib/utils';

type DatePreset = 'today' | 'week' | 'month' | 'quarter' | 'half' | 'year' | 'custom';

interface DateRange {
  start: string | null;
  end:   string | null;
}

function getPresetRange(preset: Exclude<DatePreset, 'custom'>): DateRange {
  const now   = new Date();
  const today = now.toISOString().slice(0, 10);

  switch (preset) {
    case 'today':
      return { start: today, end: today };
    case 'week': {
      const d = new Date(now);
      d.setDate(d.getDate() - 6);
      return { start: d.toISOString().slice(0, 10), end: today };
    }
    case 'month': {
      const d = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: d.toISOString().slice(0, 10), end: today };
    }
    case 'quarter': {
      const q   = Math.floor(now.getMonth() / 3);
      const d   = new Date(now.getFullYear(), q * 3, 1);
      return { start: d.toISOString().slice(0, 10), end: today };
    }
    case 'half': {
      const m = now.getMonth() < 6 ? 0 : 6;
      const d = new Date(now.getFullYear(), m, 1);
      return { start: d.toISOString().slice(0, 10), end: today };
    }
    case 'year': {
      const d = new Date(now.getFullYear(), 0, 1);
      return { start: d.toISOString().slice(0, 10), end: today };
    }
  }
}

const PRESET_LABELS: Record<Exclude<DatePreset, 'custom'>, string> = {
  today:   'اليوم',
  week:    'آخر 7 أيام',
  month:   'هذا الشهر',
  quarter: 'هذا الربع',
  half:    'نصف السنة',
  year:    'هذه السنة',
};

export default function ExecutivePage() {
  const { profile } = useAuthStore();
  const [activePreset, setActivePreset] = useState<DatePreset>('today');
  const [dateRange, setDateRange]       = useState<DateRange>(getPresetRange('today'));
  const [customStart, setCustomStart]   = useState('');
  const [customEnd,   setCustomEnd]     = useState('');
  const [customError, setCustomError]   = useState('');

  const applyPreset = useCallback((preset: Exclude<DatePreset, 'custom'>) => {
    setActivePreset(preset);
    setDateRange(getPresetRange(preset));
    setCustomError('');
  }, []);

  const applyCustom = useCallback(() => {
    if (!customStart || !customEnd) {
      setCustomError('يرجى تحديد تاريخ البداية والنهاية');
      return;
    }
    if (customEnd < customStart) {
      setCustomError('تاريخ النهاية يجب أن يكون بعد تاريخ البداية');
      return;
    }
    setCustomError('');
    setActivePreset('custom');
    setDateRange({ start: customStart, end: customEnd });
  }, [customStart, customEnd]);

  const { data, isLoading, isError, refetch } = useQuery<ExecutiveDashboardResponse>({
    queryKey: ['executive-dashboard', dateRange.start, dateRange.end],
    queryFn:  () => fetchExecutiveDashboard(dateRange.start ?? undefined, dateRange.end ?? undefined),
    staleTime: 5 * 60_000,
  });

  const handleSignOut = async () => { await supabase.auth.signOut(); };

  const kpis = data?.kpis;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-blue-950">

      {/* Header */}
      <header className="glass-panel border-b border-slate-200/80 dark:border-slate-700/50 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/munawb-logo.png" alt="" className="w-8 h-8 rounded-xl" />
            <span className="font-black text-slate-800 dark:text-white text-sm">مُناوِب — لوحة التنفيذي</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => refetch()} className="btn-secondary text-xs px-3 py-1.5" title="تحديث">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <span className="text-xs font-bold text-slate-600 dark:text-slate-400">{profile?.name}</span>
            <button onClick={handleSignOut} className="btn-secondary text-xs px-3 py-1.5">خروج</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">

        {/* Date Filter Bar */}
        <div className="glass-panel rounded-2xl p-4">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {(Object.entries(PRESET_LABELS) as [Exclude<DatePreset,'custom'>, string][]).map(([key, label]) => (
              <button
                key={key}
                onClick={() => applyPreset(key)}
                className={`px-4 py-2 rounded-xl font-black text-xs transition-all ${
                  activePreset === key
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50'
                }`}
              >
                {label}
              </button>
            ))}
            <button
              onClick={() => setActivePreset('custom')}
              className={`px-4 py-2 rounded-xl font-black text-xs transition-all ${
                activePreset === 'custom'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50'
              }`}
            >
              نطاق مخصص
            </button>
          </div>

          {/* Custom date range */}
          {activePreset === 'custom' && (
            <div className="flex flex-wrap items-end gap-3 pt-3 border-t border-slate-200 dark:border-slate-700">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">من</label>
                <input
                  type="date"
                  className="input-field py-2 text-sm"
                  dir="ltr"
                  value={customStart}
                  onChange={e => setCustomStart(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">إلى</label>
                <input
                  type="date"
                  className="input-field py-2 text-sm"
                  dir="ltr"
                  value={customEnd}
                  onChange={e => setCustomEnd(e.target.value)}
                />
              </div>
              <button onClick={applyCustom} className="btn-primary text-sm px-5 py-2">
                تطبيق
              </button>
              {customError && (
                <p className="text-red-500 text-xs font-bold w-full">{customError}</p>
              )}
            </div>
          )}
        </div>

        {/* Loading / Error */}
        {isLoading && (
          <div className="glass-panel rounded-2xl p-10 text-center">
            <span className="spinner border-blue-200 border-t-blue-600" />
            <p className="text-sm text-slate-400 font-bold mt-3">جارٍ تحميل البيانات...</p>
          </div>
        )}
        {isError && (
          <div className="glass-panel rounded-2xl p-8 text-center">
            <p className="text-red-500 font-bold text-sm mb-3">تعذّر تحميل لوحة المدير</p>
            <button onClick={() => refetch()} className="btn-primary text-sm">إعادة المحاولة</button>
          </div>
        )}

        {/* KPI Cards */}
        {!isLoading && kpis && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <KpiCard title="مفتوح"     value={kpis.total_open}      color="blue"   />
            <KpiCard title="مصعّد"     value={kpis.total_escalated} color="orange" />
            <KpiCard title="مغلق"      value={kpis.total_done}      color="green"  />
            <KpiCard title="حرج مفتوح" value={kpis.critical_risk}   color="red"    />
            <KpiCard title="عالي مفتوح" value={kpis.high_risk}      color="orange" />
            <KpiCard title="تجاوز SLA" value={kpis.breached_open}   color="red"    />
          </div>
        )}

        {/* Dept Performance + Stuck Events — two-column on large screens */}
        {!isLoading && data && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Dept Performance */}
            <div className="lg:col-span-2 glass-panel rounded-2xl p-6">
              <h2 className="text-sm font-black text-slate-800 dark:text-white mb-4">أداء الأقسام</h2>
              {!data.dept_performance?.length ? (
                <p className="text-sm text-slate-400 text-center py-6">لا توجد بيانات</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100 dark:border-slate-800">
                        <th className="pb-2 text-right font-black text-xs text-slate-500 dark:text-slate-400">القسم</th>
                        <th className="pb-2 text-center font-black text-xs text-slate-500 dark:text-slate-400">مفتوح</th>
                        <th className="pb-2 text-center font-black text-xs text-slate-500 dark:text-slate-400">مصعّد</th>
                        <th className="pb-2 text-center font-black text-xs text-slate-500 dark:text-slate-400">تجاوز SLA</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.dept_performance.map((dept, i) => (
                        <tr key={i} className="border-b border-slate-50 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                          <td className="py-2.5 font-bold text-slate-800 dark:text-white">{dept.dept_name}</td>
                          <td className="py-2.5 text-center font-black text-blue-600">{dept.open}</td>
                          <td className="py-2.5 text-center font-black text-orange-600">{dept.escalated}</td>
                          <td className="py-2.5 text-center">
                            {dept.breached > 0
                              ? <span className="font-black text-red-600">{dept.breached}</span>
                              : <span className="font-black text-green-600">0</span>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Stuck Events */}
            <div className="glass-panel rounded-2xl p-6">
              <h2 className="text-sm font-black text-slate-800 dark:text-white mb-4">أحداث عالقة (+48 ساعة)</h2>
              {!data.stuck_events?.length ? (
                <p className="text-sm text-slate-400 text-center py-6">لا توجد أحداث عالقة</p>
              ) : (
                <div className="space-y-3">
                  {data.stuck_events.map((ev, i) => {
                    const colors = RISK_LEVEL_COLORS[ev.risk] ?? RISK_LEVEL_COLORS['منخفض'];
                    return (
                      <div key={i} className="flex items-start justify-between gap-2 pb-3 border-b border-slate-100 dark:border-slate-800 last:border-0">
                        <div>
                          <p className="text-xs font-black text-slate-800 dark:text-white">{ev.event_id}</p>
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{ev.dept}</p>
                          <p className="text-[11px] text-slate-400 dark:text-slate-500">{ev.status}</p>
                        </div>
                        <div className="text-left shrink-0">
                          <span className={`text-[10px] font-black px-2 py-0.5 rounded-full border ${colors.badge}`}>
                            {ev.risk}
                          </span>
                          <p className="text-[11px] text-red-500 font-black mt-1">
                            {Math.round(ev.elapsed_hours)} ساعة
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          </div>
        )}

      </main>
    </div>
  );
}

/* ── Sub-components ─────────────────────────────────────────────── */

const KPI_COLORS = {
  blue:   'bg-blue-50   dark:bg-blue-900/20  text-blue-700   dark:text-blue-400   border-blue-200   dark:border-blue-800',
  green:  'bg-green-50  dark:bg-green-900/20 text-green-700  dark:text-green-400  border-green-200  dark:border-green-800',
  orange: 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-800',
  red:    'bg-red-50    dark:bg-red-900/20   text-red-700    dark:text-red-400    border-red-200    dark:border-red-800',
};

function KpiCard({ title, value, color }: { title: string; value: number; color: keyof typeof KPI_COLORS }) {
  return (
    <div className={`rounded-2xl p-4 border ${KPI_COLORS[color]}`}>
      <p className="text-3xl font-black">{value}</p>
      <p className="text-xs font-black mt-1 opacity-80">{title}</p>
    </div>
  );
}
